import type { ItemDef } from '../data/presets';
import type { SaveData } from './model';

/**
 * Gear upgrades (+1 to +5), paid with coins: the coin sink that makes farming
 * worth it. Each level adds one point to the item's main stat (HP items get
 * HP_PER_LEVEL). The level belongs to the item, so every hero wearing it gets it.
 * Paying for an upgrade lives in progression.ts (upgradeItem), next to buyItem.
 */

export const MAX_ITEM_LEVEL = 5;
const HP_PER_LEVEL = 4;

type Stat = 'hp' | 'atk' | 'def' | 'luck';
/** Budget cost of one point, as in the rules (HP 1, ATK 3, DEF 2, LUCK 2). */
const WEIGHT: Record<Stat, number> = { hp: 1, atk: 3, def: 2, luck: 2 };

/** The stat an upgrade raises: the item's most valuable positive bonus (ties: ATK, DEF, LUCK, HP). */
export function mainStat(item: ItemDef): Stat {
  let best: Stat = 'atk';
  let value = 0;
  for (const s of ['atk', 'def', 'luck', 'hp'] as Stat[]) {
    const v = (item.bonus[s] ?? 0) * WEIGHT[s];
    if (v > value) {
      best = s;
      value = v;
    }
  }
  return best;
}

/** Extra stats an item gets at an upgrade level. */
export function upgradeBonus(item: ItemDef, level: number): Partial<Record<Stat, number>> {
  const n = Math.max(0, Math.min(MAX_ITEM_LEVEL, Math.floor(level)));
  if (n === 0) return {};
  const s = mainStat(item);
  return { [s]: s === 'hp' ? n * HP_PER_LEVEL : n };
}

/** Coins to go from `level` to `level + 1`: a fifth of the price per step, rising each level. */
export function upgradeCost(item: ItemDef, level: number): number {
  return Math.round((item.price * 0.2 * (level + 1)) / 5) * 5;
}

export function itemLevel(save: Pick<SaveData, 'itemLevels'>, id: string): number {
  return save.itemLevels[id] ?? 0;
}
